// Topbar notifications bell — button with badge count + dropdown panel
// listing recent notifications. Clicking a row navigates to the right
// section (currently: pantry events → Food → Pantry sub-tab with the
// expiring filter). Dismissable per-row; "Mark all read" clears the
// badge but keeps rows visible until each is dismissed.

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../icons";
import { useNotifications } from "../lib/use-notifications";
import type { Notification } from "../lib/rpc";

interface NotificationsBellProps {
  /** Called for pantry.* — parent navigates to Food → Pantry. */
  onOpenPantry: () => void;
  /** Called for chore.* — parent navigates to Chores. */
  onOpenChores: () => void;
  /** Called for kidchat.* — parent opens the Family panel where
   *  flagged kid-chat messages are reviewed. */
  onOpenFamily: () => void;
  /** Called for calendar.* — parent navigates to Calendar. */
  onOpenCalendar: () => void;
}

export function NotificationsBell({ onOpenPantry, onOpenChores, onOpenFamily, onOpenCalendar }: NotificationsBellProps) {
  const n = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on outside click. Escape closes too — typical dropdown
  // affordance, doesn't fight the existing ⌘K shortcut handler.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleRowClick = (item: Notification) => {
    // Read-on-click — the user opening the row is the natural moment
    // to mark it read. Dismissal stays opt-in via the X button.
    if (!item.readAt) n.markRead(item.id).catch(() => {});
    // Route by kind prefix. Each notification category jumps to the
    // section where the user can act on it.
    if (item.kind.startsWith("pantry.")) {
      onOpenPantry();
    } else if (item.kind.startsWith("chore.")) {
      onOpenChores();
    } else if (item.kind.startsWith("kidchat.")) {
      onOpenFamily();
    } else if (item.kind.startsWith("calendar.")) {
      onOpenCalendar();
    } else {
      // Unknown kind — leave the panel open; user can dismiss manually.
      return;
    }
    setOpen(false);
  };

  const badge = n.summary.unread;
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        className="icon-btn desk-only"
        title={badge > 0 ? `${badge} new notification${badge === 1 ? "" : "s"}` : "Notifications"}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        type="button"
      >
        <Icon name="bell" />
        {badge > 0 && (
          <span className="notif-badge" aria-label={`${badge} unread`}>
            {badge > 9 ? "9+" : String(badge)}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <span className="notif-title">Notifications</span>
            {n.summary.unread > 0 && (
              <button
                type="button"
                className="notif-markall"
                onClick={() => n.markAllRead()}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-list">
            {n.loading && n.items.length === 0 && (
              <p className="notif-empty">loading…</p>
            )}
            {!n.loading && n.items.length === 0 && (
              <p className="notif-empty">Nothing new.</p>
            )}
            {n.items.map((item) => (
              <Row
                key={item.id}
                item={item}
                onClick={() => handleRowClick(item)}
                onDismiss={() => n.dismiss(item.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  item, onClick, onDismiss,
}: {
  item: Notification;
  onClick: () => void;
  onDismiss: () => void;
}) {
  const { title, body, tone } = renderNotification(item);
  return (
    <div
      className="notif-row"
      data-unread={!item.readAt}
      data-tone={tone}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    >
      <div className="notif-row-body">
        <div className="notif-row-title">{title}</div>
        {body && <div className="notif-row-text">{body}</div>}
        <div className="notif-row-time">{relativeTime(item.createdAt)}</div>
      </div>
      <button
        type="button"
        className="notif-row-x"
        title="Dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
      >
        <Icon name="x" size={11} />
      </button>
    </div>
  );
}

// Format a notification for rendering — kind-specific copy lives here
// so the row component stays generic. Each new producer adds a case.
function renderNotification(n: Notification): { title: string; body: string; tone: "info" | "warn" | "alert" } {
  const p = n.payload || {};
  if (n.kind === "pantry.expiring") {
    return {
      title: `${p.name || "Pantry item"} expires soon`,
      body: p.expires ? `${p.qty || ""} · expires ${p.expires}`.trim() : (p.qty || ""),
      tone: "warn",
    };
  }
  if (n.kind === "pantry.expired") {
    return {
      title: `${p.name || "Pantry item"} expired`,
      body: p.expires ? `${p.qty || ""} · was ${p.expires}`.trim() : (p.qty || ""),
      tone: "alert",
    };
  }
  if (n.kind === "kidchat.flagged") {
    const cats = Array.isArray(p.categories) ? p.categories.map((c: string) => c.replace(/_/g, " ")).join(", ") : "";
    return {
      title: `Flagged message in ${p.kidUserId || "a kid"}'s chat`,
      body: [cats, p.snippet ? `"${p.snippet}"` : ""].filter(Boolean).join(" — ").slice(0, 160) || "Review in Family.",
      tone: "alert",
    };
  }
  if (n.kind === "chore.pending_approval") {
    // "Ezra finished 'Empty dishwasher' — approve to grant stars"
    const who = p.completedByName || p.completedByUserId || "Someone";
    const chore = p.choreName || "a chore";
    return {
      title: `${who} finished "${chore}"`,
      body: "Review in Chores to grant the stars.",
      tone: "info",
    };
  }
  // Default fallback — best-effort copy for unknown kinds.
  return {
    title: n.kind,
    body: JSON.stringify(p).slice(0, 120),
    tone: "info",
  };
}

function relativeTime(iso: string): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d`;
  return `${Math.floor(day / 7)}w`;
}
